/* global BigInt */
/**
 * Position Math Utilities
 * All calculations use raw lamports and raw token atoms internally.
 * Functions accept strings or numbers and return strings for precision.
 * Database stores as int8/numeric, convert when reading/writing.
 */

const LAMPORTS_PER_SOL = 1_000_000_000n;
const TOKEN_DECIMALS = 6;

/**
 * Safely parse a value to BigInt, returning 0n if invalid
 */
function safeBigInt(value) {
  if (value === null || value === undefined || value === '') {
    return 0n;
  }

  try {
    return window.BigInt(value);
  } catch {
    return 0n;
  }
}

/**
 * Calculate weighted average entry price after a buy
 * @param {string|bigint} existingCostBasis - current cost basis in lamports
 * @param {string|bigint} existingTokenBalance - current token balance in atoms
 * @param {string|bigint} newSolSpent - SOL spent on new buy in lamports
 * @param {string|bigint} newTokensBought - tokens received in atoms
 * @returns {string} new average entry in lamports per token (scaled)
 */
export function calculateWeightedAvgEntry(
  existingCostBasis,
  existingTokenBalance,
  newSolSpent,
  newTokensBought
) {
  const currentCostBasis = safeBigInt(existingCostBasis);
  const currentBalance = safeBigInt(existingTokenBalance);
  const solSpent = safeBigInt(newSolSpent);
  const tokensBought = safeBigInt(newTokensBought);

  const totalCostBasis = currentCostBasis + solSpent;
  const totalTokens = currentBalance + tokensBought;

  if (totalTokens === 0n) {
    return '0';
  }

  // Scale up to preserve precision: avg_entry = (total_cost * 1e6) / total_tokens
  const avgEntry = (totalCostBasis * 1_000_000n) / totalTokens;

  return avgEntry.toString();
}

/**
 * Calculate cost basis reduction after a sell
 * Uses average cost basis to determine how much cost to remove
 * @param {string|bigint} existingCostBasis - current cost basis in lamports
 * @param {string|bigint} avgEntry - average entry lamports per token (scaled by 1e6)
 * @param {string|bigint} tokensSold - tokens sold in atoms
 * @returns {string} new cost basis in lamports
 */
export function calculateCostBasisAfterSell(existingCostBasis, avgEntry, tokensSold) {
  const currentCostBasis = safeBigInt(existingCostBasis);
  const avgEntryScaled = safeBigInt(avgEntry);
  const soldTokens = safeBigInt(tokensSold);

  if (soldTokens === 0n || avgEntryScaled === 0n) {
    return currentCostBasis.toString();
  }

  // Cost to remove = (tokens_sold * avg_entry) / 1e6
  const costToRemove = (soldTokens * avgEntryScaled) / 1_000_000n;
  const newCostBasis = currentCostBasis > costToRemove ? currentCostBasis - costToRemove : 0n;

  return newCostBasis.toString();
}

/**
 * Calculate realized P&L after a sell
 * @param {string|bigint} existingRealizedPnL - current realized P&L in lamports
 * @param {string|bigint} solReceived - SOL received from sell in lamports
 * @param {string|bigint} avgEntry - average entry lamports per token (scaled by 1e6)
 * @param {string|bigint} tokensSold - tokens sold in atoms
 * @returns {string} new realized P&L in lamports
 */
export function calculateRealizedPnL(existingRealizedPnL, solReceived, avgEntry, tokensSold) {
  const currentRealizedPnL = safeBigInt(existingRealizedPnL);
  const receivedLamports = safeBigInt(solReceived);
  const avgEntryScaled = safeBigInt(avgEntry);
  const soldTokens = safeBigInt(tokensSold);

  if (soldTokens === 0n || avgEntryScaled === 0n) {
    return currentRealizedPnL.toString();
  }

  // Cost basis of sold tokens = (tokens_sold * avg_entry) / 1e6
  const costOfSold = (soldTokens * avgEntryScaled) / 1_000_000n;

  // P&L from this sell = received - cost
  const sellPnL = receivedLamports - costOfSold;

  // Add to existing realized P&L (can be negative)
  const newRealizedPnL = currentRealizedPnL + sellPnL;

  return newRealizedPnL.toString();
}

/**
 * Calculate current value of a token position
 * Uses sell quote logic to determine what the position would sell for
 * @param {string|bigint} tokenBalance - token balance in atoms
 * @param {object} currentGameState - live game state
 * @param {object} globalConfig - global config
 * @returns {string|null} current value in lamports, or null if unable to calculate
 */
export function calculateCurrentValue(tokenBalance, currentGameState, globalConfig) {
  const balance = safeBigInt(tokenBalance);

  if (balance === 0n || !currentGameState || !globalConfig) {
    return null;
  }

  try {
    // Import sell quote logic (we'll use the existing getSellQuote function)
    // For now, return null - this will be computed in the hook using existing quote logic
    return null;
  } catch {
    return null;
  }
}

/**
 * Calculate unrealized P&L
 * @param {string|bigint} currentValue - current value in lamports
 * @param {string|bigint} costBasis - cost basis in lamports
 * @returns {string|null} unrealized P&L in lamports
 */
export function calculateUnrealizedPnL(currentValue, costBasis) {
  const value = safeBigInt(currentValue);
  const cost = safeBigInt(costBasis);

  if (value === 0n && cost === 0n) {
    return null;
  }

  const unrealizedPnL = value - cost;

  return unrealizedPnL.toString();
}

/**
 * Calculate unrealized P&L percentage
 * @param {string|bigint} unrealizedPnL - unrealized P&L in lamports
 * @param {string|bigint} costBasis - cost basis in lamports
 * @returns {number|null} unrealized P&L percentage
 */
export function calculateUnrealizedPnLPercent(unrealizedPnL, costBasis) {
  const pnl = safeBigInt(unrealizedPnL);
  const cost = safeBigInt(costBasis);

  if (cost === 0n) {
    return null;
  }

  // Calculate percentage: (pnl / cost) * 100
  // Use basis points for precision: (pnl * 10000) / cost
  const basisPoints = (pnl * 10000n) / cost;
  const percent = Number(basisPoints) / 100;

  return percent;
}

/**
 * Convert raw token atoms to display tokens (6 decimals)
 * @param {string|bigint} atoms - token amount in atoms
 * @returns {string} formatted token amount
 */
export function convertAtomsToDisplay(atoms) {
  const atomsBigInt = safeBigInt(atoms);

  if (atomsBigInt === 0n) {
    return '0';
  }

  const atomsString = atomsBigInt.toString();
  const wholePart = atomsString.length > TOKEN_DECIMALS ? atomsString.slice(0, -TOKEN_DECIMALS) : '0';
  const fractionalPart = atomsString.slice(-TOKEN_DECIMALS).padStart(TOKEN_DECIMALS, '0');
  const trimmedFractionalPart = fractionalPart.replace(/0+$/, '');

  if (!trimmedFractionalPart) {
    return wholePart;
  }

  return `${wholePart}.${trimmedFractionalPart}`;
}

/**
 * Convert raw lamports to display SOL (9 decimals)
 * @param {string|bigint} lamports - amount in lamports
 * @param {number} maxDecimals - maximum decimal places to show (default: 4)
 * @returns {string} formatted SOL amount
 */
export function convertLamportsToDisplay(lamports, maxDecimals = 4) {
  const lamportsBigInt = safeBigInt(lamports);

  if (lamportsBigInt === 0n) {
    return '0';
  }

  const lamportsString = lamportsBigInt.toString();
  const isNegative = lamportsString.startsWith('-');
  const absString = isNegative ? lamportsString.slice(1) : lamportsString;
  const wholePart = absString.length > 9 ? absString.slice(0, -9) : '0';
  const fractionalPart = absString.slice(-9).padStart(9, '0');
  const trimmedFractionalPart = fractionalPart.slice(0, maxDecimals).replace(/0+$/, '');

  let result;
  if (!trimmedFractionalPart) {
    result = wholePart;
  } else {
    result = `${wholePart}.${trimmedFractionalPart}`;
  }

  return isNegative ? `-${result}` : result;
}

/**
 * Convert raw lamports per token (scaled by 1e6) to display price per token
 * @param {string|bigint} lamportsPerToken - lamports per token scaled by 1e6
 * @returns {string} formatted price
 */
export function convertAvgEntryToDisplay(lamportsPerToken) {
  const scaled = safeBigInt(lamportsPerToken);

  if (scaled === 0n) {
    return '0';
  }

  // Unscale: divide by 1e6, then convert lamports to SOL (divide by 1e9)
  // Combined: divide by 1e15
  const lamportsPerTokenUnscaled = scaled / 1_000_000n;

  return convertLamportsToDisplay(lamportsPerTokenUnscaled, 9);
}
